class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  // The overlay is not a scroll container, so a wheel or touch gesture over it
  // goes straight to the document. Swallowing those events keeps the page still
  // even if the body lock has not been applied yet.
  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.addEventListener('click', this.close.bind(this));
    overlay.addEventListener('wheel', (event) => event.preventDefault(), { passive: false });
    overlay.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        // The container can be gone when the drawer was re-rendered while sliding open
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockBodyScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockBodyScroll();
  }

  // `overflow: hidden` on the body is ignored by touch scrolling on iOS, so the
  // page keeps moving behind the drawer. Pinning the body and offsetting it by
  // the current scroll position holds it still on every platform.
  lockBodyScroll() {
    if (document.body.classList.contains('cart-drawer-open')) return;

    CartDrawer.savedScrollTop = window.scrollY || document.documentElement.scrollTop || 0;

    // Compensate for the scrollbar that disappears, so the page behind does not shift.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    document.body.style.top = `-${CartDrawer.savedScrollTop}px`;
    document.body.classList.add('cart-drawer-open', 'overflow-hidden');
  }

  unlockBodyScroll() {
    if (!document.body.classList.contains('cart-drawer-open')) return;

    document.body.classList.remove('cart-drawer-open', 'overflow-hidden');
    document.body.style.top = '';
    document.body.style.paddingRight = '';

    window.scrollTo(0, CartDrawer.savedScrollTop || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function getCartItemsElement() {
  return document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
}

// Shared by any control (clear cart, discount apply/remove) that mutates the
// cart out-of-band and needs the drawer to reflect the new state. Replaces
// the same sections a normal add/change/remove would, from an already-parsed
// cart response, and republishes the update for other listeners.
function renderCartDrawerSections(sectionsToRender, parsedState) {
  document
    .querySelectorAll('cart-drawer, cart-drawer-items, cart-items, #main-cart-footer')
    .forEach((element) => element.classList.toggle('is-empty', parsedState.item_count === 0));

  sectionsToRender.forEach((section) => {
    const container = document.getElementById(section.id);
    const sectionHTML = parsedState.sections[section.section];
    if (!container || !sectionHTML) return;

    const elementToReplace = container.querySelector(section.selector) || container;
    const source = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(section.selector);
    if (source) elementToReplace.innerHTML = source.innerHTML;
  });

  // Publishing as 'cart-items' lets other subscribers react while stopping
  // CartItems from re-fetching the sections that were just rendered.
  publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
}

class CartClearButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const cartItems = getCartItemsElement();
    if (!cartItems) return;

    this.button.setAttribute('aria-disabled', 'true');
    this.classList.add('is-loading');

    const sectionsToRender = cartItems.getSectionsToRender();

    fetch(`${routes.cart_clear_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.text())
      .then((state) => renderCartDrawerSections(sectionsToRender, JSON.parse(state)))
      .catch((e) => console.error(e))
      .finally(() => {
        this.classList.remove('is-loading');
        this.button.removeAttribute('aria-disabled');
      });
  }
}

if (!customElements.get('cart-clear-button')) {
  customElements.define('cart-clear-button', CartClearButton);
}

class CartDiscountCode extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('.cart-discount__form');
    this.input = this.querySelector('.cart-discount__input');
    this.applyButton = this.querySelector('.cart-discount__apply');
    this.errorElement = this.querySelector('.cart-discount__error');

    if (this.form) this.form.addEventListener('submit', this.onApply.bind(this));

    this.querySelectorAll('.cart-discount__remove').forEach((button) =>
      button.addEventListener('click', this.onRemove.bind(this))
    );
  }

  // Codes currently on the cart, as rendered by Liquid.
  get appliedCodes() {
    return (this.dataset.discountCodes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
  }

  onApply(event) {
    event.preventDefault();
    if (this.applyButton.getAttribute('aria-disabled') === 'true') return;

    const code = this.input.value.trim();
    if (!code) return;

    this.hideError();
    this.setLoading(this.applyButton, true);

    // Codes are sent as a full comma-separated list, so keep the existing ones.
    const codes = this.appliedCodes.slice();
    if (!codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) codes.push(code);

    const previousCodes = this.appliedCodes;
    this.postDiscount(codes.join(','))
      .then((parsedState) => {
        // The cart JSON reports discounts differently depending on whether they
        // target the order or specific line items, so it is not a reliable
        // success signal. Liquid has just re-rendered the list of codes that
        // actually stuck — read the answer from there instead.
        const renderedCodes = this.codesRenderedIn(parsedState);
        const isStuck = (applied) => applied.some((existing) => existing.toLowerCase() === code.toLowerCase());
        if (renderedCodes && !isStuck(renderedCodes)) {
          // Shopify keeps a rejected code on the cart as "not applicable" and would carry it to checkout,
          // so put the previous list back before showing the error (one render, no flash of the bad state).
          return this.postDiscount(previousCodes.join(',')).then((restored) => {
            this.render(restored);
            this.showRejected(code);
          });
        }

        this.render(parsedState);
        if (renderedCodes) return;
        const refreshed = document.querySelector('cart-discount-code');
        if (refreshed && !isStuck(refreshed.appliedCodes)) this.showRejected(code);
      })
      .catch(() => this.showError('Something went wrong. Please try again.'))
      .finally(() => this.setLoading(this.applyButton, false));
  }

  onRemove(event) {
    event.preventDefault();

    const button = event.currentTarget;
    if (button.getAttribute('aria-disabled') === 'true') return;

    const codeToRemove = (button.dataset.discountCode || '').toLowerCase();
    const remaining = this.appliedCodes.filter((code) => code.toLowerCase() !== codeToRemove);

    this.hideError();
    this.setLoading(button, true);

    this.postDiscount(remaining.join(','))
      .then((parsedState) => this.render(parsedState))
      .catch(() => this.showError('Something went wrong. Please try again.'))
      .finally(() => this.setLoading(button, false));
  }

  postDiscount(discount) {
    const cartItems = getCartItemsElement();
    const sectionsToRender = cartItems ? cartItems.getSectionsToRender() : [];
    this.pendingSections = sectionsToRender;

    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount,
        sections: sectionsToRender.map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    }).then((response) => response.json());
  }

  render(parsedState) {
    renderCartDrawerSections(this.pendingSections || [], parsedState);
  }

  // The applied codes as Liquid rendered them in an update response's sections, or null if no section has the list
  codesRenderedIn(parsedState) {
    const sections = (parsedState && parsedState.sections) || {};
    for (const html of Object.values(sections)) {
      if (!html || !html.includes('cart-discount-code')) continue;
      const element = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-discount-code');
      if (element) {
        return (element.dataset.discountCodes || '')
          .split(',')
          .map((applied) => applied.trim())
          .filter(Boolean);
      }
    }
    return null;
  }

  // Shows the error on the freshly rendered field, with the code put back so a near-miss typo can be corrected
  showRejected(code) {
    const refreshed = document.querySelector('cart-discount-code');
    if (!refreshed) return;
    if (refreshed.input) refreshed.input.value = code;
    refreshed.showError("That code isn't valid for this cart.");
  }

  setLoading(button, isLoading) {
    // While a code change is on its way, the other chips / Apply wait (see .cart-discount.is-busy): each request
    // sends the whole code list as currently shown, so a second click mid-request would undo the first
    this.classList.toggle('is-busy', isLoading);
    if (!button) return;
    button.classList.toggle('loading', isLoading);
    if (isLoading) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.hidden = true;
  }
}

if (!customElements.get('cart-discount-code')) {
  customElements.define('cart-discount-code', CartDiscountCode);
}
